import {
    ChangeEvent,
    KeyboardEvent,
    ReactNode,
    useEffect,
    useId,
    useMemo,
    useRef,
    useState,
} from 'react';

import Dropdown from 'react-bootstrap/Dropdown';

type SearchableListProps<T> = {
    items: T[];
    getKey: (item: T) => string;
    getLabel: (item: T) => string;
    onSelect: (item: T) => void;

    buttonLabel: ReactNode;
    emptyLabel?: ReactNode;
    noResultsLabel?: ReactNode;
    searchPlaceholder?: string;
    searchAriaLabel?: string;

    sorted?: boolean;
    className?: string;
    disabled?: boolean;
    maxHeight?: number;
    minWidth?: number;
};

function SearchableList<T>({
    items,
    getKey,
    getLabel,
    onSelect,
    buttonLabel,
    emptyLabel = 'No items available.',
    noResultsLabel = 'No matching items.',
    searchPlaceholder = 'Search...',
    searchAriaLabel = 'Search items',
    sorted = false,
    className,
    disabled = false,
    maxHeight = 300,
    minWidth = 280,
}: SearchableListProps<T>) {
    const [search, setSearch] = useState('');
    const [isOpen, setIsOpen] = useState(false);

    const searchInputId = useId();
    const searchInputRef = useRef<HTMLInputElement>(null);
    const firstItemRef = useRef<HTMLButtonElement>(null);

    const filteredItems = useMemo(() => {
        const normalizedSearch = search
            .trim()
            .toLowerCase();

        let result = !normalizedSearch
            ? items
            : items.filter((item) =>
                getLabel(item)
                    .toLowerCase()
                    .includes(normalizedSearch),
            );

        if (sorted) {
            result = [...result].sort((left, right) =>
                getLabel(left).localeCompare(getLabel(right)),
            );
        }

        return result;
    }, [items, search, getLabel, sorted]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        requestAnimationFrame(() => {
            searchInputRef.current?.focus();
        });
    }, [isOpen]);

    useEffect(() => {
        if (disabled) {
            setIsOpen(false);
            setSearch('');
        }
    }, [disabled]);

    const handleToggle = (nextIsOpen: boolean) => {
        setIsOpen(nextIsOpen);

        if (!nextIsOpen) {
            setSearch('');
        }
    };

    const handleSearchChange = (
        event: ChangeEvent<HTMLInputElement>,
    ) => {
        setSearch(event.target.value);
    };

    const handleSearchKeyDown = (
        event: KeyboardEvent<HTMLInputElement>,
    ) => {
        if (
            event.key === 'ArrowDown' &&
            filteredItems.length > 0
        ) {
            event.preventDefault();
            firstItemRef.current?.focus();
        }
    };

    const handleSelect = (item: T) => {
        onSelect(item);
        setIsOpen(false);
        setSearch('');
    };

    return (
        <Dropdown
            className={className}
            show={isOpen}
            onToggle={handleToggle}
            align="end"
            autoClose
        >
            <Dropdown.Toggle
                size="sm"
                variant="outline-primary"
                disabled={disabled}
                aria-expanded={isOpen}
            >
                {buttonLabel}
            </Dropdown.Toggle>

            <Dropdown.Menu
                className="p-2"
                style={{ minWidth }}
            >
                <label
                    className="visually-hidden"
                    htmlFor={searchInputId}
                >
                    {searchAriaLabel}
                </label>

                <input
                    ref={searchInputRef}
                    id={searchInputId}
                    type="search"
                    className="form-control form-control-sm mb-2"
                    value={search}
                    onChange={handleSearchChange}
                    onKeyDown={handleSearchKeyDown}
                    onClick={(event) =>
                        event.stopPropagation()
                    }
                    placeholder={searchPlaceholder}
                    aria-label={searchAriaLabel}
                />

                <div
                    style={{
                        maxHeight,
                        overflowY: 'auto',
                    }}
                >
                    {filteredItems.map((item, index) => (
                        <Dropdown.Item
                            ref={
                                index === 0
                                    ? firstItemRef
                                    : undefined
                            }
                            key={getKey(item)}
                            as="button"
                            type="button"
                            className="rounded"
                            onClick={() =>
                                handleSelect(item)
                            }
                        >
                            {getLabel(item)}
                        </Dropdown.Item>
                    ))}

                    {filteredItems.length === 0 && (
                        <div className="px-3 py-2 text-body-secondary small">
                            {search
                                ? noResultsLabel
                                : emptyLabel}
                        </div>
                    )}
                </div>
            </Dropdown.Menu>
        </Dropdown>
    );
}

export default SearchableList;